import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { getDoctorAppointmentsInRange } from '../services/consultationApi';
import { getExportRows, downloadScheduleCsv } from '../services/availabilityApi';
import AppointmentCalendar from '../components/AppointmentCalendar';
import SlotConfigPanel from '../components/SlotConfigPanel';
import AddSlotModal from '../components/AddSlotModal';

const STATUS_META = {
  CONFIRMED: { label: 'Confirmed', color: '#059669', bg: '#ecfdf5', border: '#6ee7b7' },
  PENDING:   { label: 'Pending',   color: '#d97706', bg: '#fffbeb', border: '#fde68a' },
  COMPLETED: { label: 'Completed', color: '#6366f1', bg: '#eef2ff', border: '#c7d2fe' },
  CANCELLED: { label: 'Cancelled', color: '#dc2626', bg: '#fef2f2', border: '#fca5a5' },
  NO_SHOW:   { label: 'No Show',   color: '#92400e', bg: '#fef3c7', border: '#fde68a' },
};

const toneForStatus = (status) => {
  if (status === 'COMPLETED') return 'blue';
  if (status === 'CANCELLED' || status === 'NO_SHOW') return 'red';
  return 'green';
};

const pad = (n) => String(n).padStart(2, '0');
const monthRange = (year, month) => {
  const start = `${year}-${pad(month + 1)}-01`;
  const endDay = new Date(year, month + 1, 0).getDate();
  const end = `${year}-${pad(month + 1)}-${pad(endDay)}`;
  return { start, end };
};
const todayISO = () => {
  const d = new Date();
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
};

const DoctorSchedule = () => {
  const navigate = useNavigate();
  const now = new Date();
  const [year, setYear] = useState(now.getFullYear());
  const [month, setMonth] = useState(now.getMonth());
  const [selectedDate, setSelectedDate] = useState(todayISO());
  const [appointments, setAppointments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [exporting, setExporting] = useState(false);
  const [showAddSlot, setShowAddSlot] = useState(false);
  const [refreshTick, setRefreshTick] = useState(0);

  const load = useCallback(async (y, m) => {
    setLoading(true);
    setError(null);
    try {
      const { start, end } = monthRange(y, m);
      const data = await getDoctorAppointmentsInRange(start, end);
      setAppointments(data);
    } catch (e) {
      setError('Could not load your schedule. Please try again.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(year, month); }, [year, month, load, refreshTick]);

  const handleMonthChange = (y, m) => { setYear(y); setMonth(m); };

  const handleConfigChanged = () => setRefreshTick((t) => t + 1);

  const handleExport = async () => {
    setExporting(true);
    setError(null);
    try {
      const { start, end } = monthRange(year, month);
      const rows = await getExportRows(start, end);
      const monthName = new Date(year, month, 1).toLocaleDateString(undefined, { month: 'short', year: 'numeric' });
      downloadScheduleCsv(rows, `schedule-${monthName.replace(' ', '-')}.csv`);
    } catch (e) {
      setError('Could not export your schedule. Please try again.');
    } finally {
      setExporting(false);
    }
  };

  const events = appointments
    .filter(a => a.status !== 'CANCELLED')
    .map(a => ({
      date: a.appointmentDate,
      label: `${a.slotTime || ''} ${a.patientName || 'Patient'}`.trim(),
      tone: toneForStatus(a.status),
    }))
    .concat(
      appointments
        .filter(a => a.status === 'CANCELLED')
        .map(a => ({ date: a.appointmentDate, label: `${a.slotTime || ''} Cancelled`.trim(), tone: 'red' }))
    );

  const dayAppointments = appointments
    .filter(a => a.appointmentDate === selectedDate)
    .sort((a, b) => (a.slotTime || '').localeCompare(b.slotTime || ''));

  const selectedLabel = selectedDate
    ? new Date(selectedDate + 'T00:00:00').toLocaleDateString(undefined, { weekday: 'long', month: 'short', day: 'numeric' })
    : '';

  const goToAppointment = (appt) => {
    if (appt.consultationSubmitted) {
      navigate(`/doctor/consultation/${appt.id}/results`);
    } else {
      navigate(`/doctor/consultation/${appt.id}`);
    }
  };

  return (
    <div className="page">
      <main className="page-main">
        <div style={{ marginBottom: '1.5rem', display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 12 }}>
          <div>
            <h1 className="t-h1" style={{ marginBottom: 4 }}>📅 My Schedule & Availability</h1>
            <p className="t-body-sm">View your appointments and configure your availability</p>
          </div>
          <div style={{ display: 'flex', gap: 10 }}>
            <button className="btn-outline btn-sm" onClick={handleExport} disabled={exporting}>
              {exporting ? 'Exporting…' : '📥 Export'}
            </button>
            <button className="btn-primary btn-sm" onClick={() => setShowAddSlot(true)}>
              + Add Slot
            </button>
          </div>
        </div>

        {error && (
          <div className="error-state" style={{ marginBottom: '1rem' }}>
            <span>⚠️</span> {error}
            <button className="t-link" style={{ marginLeft: 12 }} onClick={() => load(year, month)}>Retry</button>
          </div>
        )}

        <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0,1fr) 300px', gap: 18, alignItems: 'flex-start' }}>
          <AppointmentCalendar
            year={year}
            month={month}
            events={loading ? [] : events}
            selectedDate={selectedDate}
            onSelectDate={setSelectedDate}
            onMonthChange={handleMonthChange}
          />

          <div style={{ display: 'flex', flexDirection: 'column', gap: 18 }}>
            <div style={{ background: '#fff', borderRadius: 16, border: '1px solid #f3f4f6', padding: 18, fontFamily: 'DM Sans, sans-serif' }}>
              <div style={{ fontFamily: 'Sora, sans-serif', fontWeight: 700, fontSize: 14, color: '#1e1b4b', marginBottom: 12 }}>
                {selectedLabel || 'Select a date'}
              </div>

              {loading ? (
                <div style={{ fontSize: 12.5, color: '#9ca3af' }}>Loading…</div>
              ) : dayAppointments.length === 0 ? (
                <div style={{ fontSize: 12.5, color: '#9ca3af' }}>No appointments on this day.</div>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                  {dayAppointments.map(appt => {
                    const meta = STATUS_META[appt.status] || STATUS_META.CONFIRMED;
                    return (
                      <div
                        key={appt.id}
                        onClick={() => goToAppointment(appt)}
                        style={{
                          border: '1px solid #f3f4f6', borderRadius: 12, padding: '10px 12px', cursor: 'pointer',
                        }}
                      >
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 4 }}>
                          <span style={{ fontSize: 12.5, fontWeight: 700, color: '#1e1b4b' }}>{appt.slotTime || '—'}</span>
                          <span style={{
                            fontSize: 10, fontWeight: 600, padding: '2px 8px', borderRadius: 9999,
                            background: meta.bg, color: meta.color, border: `1px solid ${meta.border}`,
                          }}>
                            {meta.label}
                          </span>
                        </div>
                        <div style={{ fontSize: 12.5, color: '#374151' }}>{appt.patientName || 'Patient'}</div>
                        {appt.symptoms && (
                          <div style={{ fontSize: 11, color: '#9ca3af', marginTop: 2, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                            {appt.symptoms}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>

            <SlotConfigPanel selectedDate={selectedDate} onChanged={handleConfigChanged} />
          </div>
        </div>
      </main>

      {showAddSlot && (
        <AddSlotModal
          initialDate={selectedDate}
          onClose={() => setShowAddSlot(false)}
          onAdded={handleConfigChanged}
        />
      )}
    </div>
  );
};

export default DoctorSchedule;